import type { QuickSearchRegistryResult } from '@/components/quick-search/quick-search-types';
import type { QuickSearchResult } from '@/components/quick-search/quick-search-types';
import {
  QUICK_SEARCH_ENTITY_GROUP_ORDER,
  QUICK_SEARCH_NAV_GROUP_ORDER,
} from '@/components/quick-search/quick-search-types';

const REGISTRY_EXACT_SCORE = 55;
const REGISTRY_PREFIX_SCORE = 45;
const REGISTRY_CONTAINS_SCORE = 35;

export function scoreRegistryResult(result: QuickSearchRegistryResult, query: string): number {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return 0;
  const title = result.title.trim().toLowerCase();
  if (title === normalized) return REGISTRY_EXACT_SCORE;
  if (title.startsWith(normalized)) return REGISTRY_PREFIX_SCORE;
  if (title.includes(normalized)) return REGISTRY_CONTAINS_SCORE;
  if (result.keywords.some((keyword) => keyword.includes(normalized))) return REGISTRY_CONTAINS_SCORE;
  if (result.subtitle?.toLowerCase().includes(normalized)) return REGISTRY_CONTAINS_SCORE;
  return 0;
}

export function mergeQuickSearchResults(input: {
  query: string;
  registryResults: QuickSearchRegistryResult[];
  serverResults: QuickSearchResult[];
}): QuickSearchResult[] {
  const shortcuts = input.serverResults.filter((result) => result.kind === 'shortcut');
  const serverEntities = input.serverResults.filter((result) => result.kind !== 'shortcut');

  const registryScored = input.registryResults.map((result) => ({
    ...result,
    score: scoreRegistryResult(result, input.query),
  }));

  const combined = [...serverEntities, ...registryScored];
  combined.sort(
    (a, b) => (b.score ?? 0) - (a.score ?? 0) || a.title.localeCompare(b.title),
  );
  return [...combined, ...shortcuts];
}

const RELATED_GROUP_PREFIX = 'Related to ';

function isRelatedGroup(group: string): boolean {
  return group.startsWith(RELATED_GROUP_PREFIX);
}

export function resolveQuickSearchGroupOrder(results: QuickSearchResult[]): readonly string[] {
  const grouped = groupQuickSearchResults(results);
  const presentGroups = new Set(grouped.keys());

  const relatedGroups = [...presentGroups]
    .filter(isRelatedGroup)
    .sort((a, b) => a.localeCompare(b));

  const hasEntityResults = results.some(
    (result) => result.source === 'server' && result.kind === 'entity',
  );

  const baseOrder = hasEntityResults
    ? [...QUICK_SEARCH_ENTITY_GROUP_ORDER, ...QUICK_SEARCH_NAV_GROUP_ORDER]
    : [...QUICK_SEARCH_NAV_GROUP_ORDER, ...QUICK_SEARCH_ENTITY_GROUP_ORDER];

  const order: string[] = [];
  for (const group of baseOrder) {
    order.push(group);
    if (group === 'Sites') {
      order.push(...relatedGroups);
    }
  }

  for (const group of presentGroups) {
    if (!order.includes(group)) {
      order.push(group);
    }
  }

  return order;
}

export function groupQuickSearchResults(results: QuickSearchResult[]): Map<string, QuickSearchResult[]> {
  const grouped = new Map<string, QuickSearchResult[]>();
  for (const result of results) {
    const bucket = grouped.get(result.group) ?? [];
    bucket.push(result);
    grouped.set(result.group, bucket);
  }
  return grouped;
}
